export interface BarcodeComponents {
  SKU: number
  PriceOrWeight: string
}

/**
 * @class ProductEmbeddedBarcodeService
 */
export class ProductEmbeddedBarcodeService {
  public ean13EmbeddedBarcodeMatcher: RegExp
  constructor() {
    'ngInject'
    // EAN13 embedded barcodes are "20" followed by 11 digits
    this.ean13EmbeddedBarcodeMatcher = /^(?:20|02|2)([0-9]{5})([0-9]{5})[0-9]$/
  }

  /**
   * Identifies a valid embedded barcode.
   *
   * @method isEmbeddedBarcode
   *
   * @param {String} query
   *        The query to evaluate.
   *
   * @return {Boolean} True if the provided query is a supported embedded barcode.
   */
  public isEmbeddedBarcode(query: string) {
    return (
      this.ean13EmbeddedBarcodeMatcher.test(query) &&
      this._validateChecksum(query)
    )
  }

  /**
   * Extracts components
   */
  public extractComponents(query: string): BarcodeComponents | undefined {
    if (!this.ean13EmbeddedBarcodeMatcher.test(query)) {
      return undefined
    }

    const regexResult = this.ean13EmbeddedBarcodeMatcher.exec(query)

    if (!regexResult || regexResult.length < 3) {
      return undefined
    }

    // We use parseInt to remove leading 0s.
    return {
      SKU: parseInt(regexResult[1], 10),
      PriceOrWeight: String(parseInt(regexResult[2], 10)),
    }
  }

  /**
   * Validates the checksum (Modulo 10)
   * GTIN implementation factor 3
   *
   * @param  {String} value The barcode to validate
   * @return {Boolean}
   * @api private
   */
  private _validateChecksum(value: string) {
    const barcode = value.substring(0, value.length - 1)
    const checksum = parseInt(value.substring(value.length - 1), 10)
    let calcSum = 0
    let calcChecksum = 0

    barcode.split('').forEach((numberStr, index) => {
      const number = parseInt(numberStr, 10)
      if (value.length % 2 === 0) {
        index += 1
      }
      if (index % 2 === 0) {
        calcSum += number
      } else {
        calcSum += number * 3
      }
    })

    calcSum %= 10
    calcChecksum = calcSum === 0 ? 0 : 10 - calcSum

    if (calcChecksum !== checksum) {
      return false
    }

    return true
  }
}
